import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  RadioGroup, RadioGroupItem,
} from "./ui/radio-group";
import { Heart, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const QUICK_AMOUNTS = [3, 5, 10, 20];
const PAYMENT_LABELS = {
  cashapp: "Cash App",
  venmo: "Venmo",
  zelle: "Zelle",
  paypal: "PayPal",
  apple_pay: "Apple Pay",
  in_person: "In-person",
};

/**
 * TipDialog
 *  - vendor: the vendor object (must have user_id + vendor_profile.payment_handles)
 *  - trigger: optional custom trigger element. Defaults to a pink "Send Tip" button.
 */
export default function TipDialog({ vendor, trigger, onTipped, source = "storefront" }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(5);
  const [custom, setCustom] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handles = vendor?.vendor_profile?.payment_handles || {};
  const availableMethods = Object.entries(handles).filter(([, v]) => v && String(v).trim() !== "");

  const [method, setMethod] = useState(availableMethods[0]?.[0] || "cashapp");

  const finalAmount = custom ? parseFloat(custom) : amount;

  const submit = async () => {
    if (!finalAmount || finalAmount <= 0) {
      toast.error("Enter a tip amount");
      return;
    }
    if (!method) {
      toast.error("Pick a payment method");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/vendors/${vendor.user_id}/tip`, {
        amount: finalAmount,
        payment_method: method,
        note,
      });
      toast.success(`Tip sent! Pay $${finalAmount.toFixed(2)} via ${PAYMENT_LABELS[method]}`);
      setOpen(false);
      setCustom("");
      setNote("");
      onTipped?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send tip");
    } finally {
      setSubmitting(false);
    }
  };

  const defaultTrigger = (
    <Button
      type="button"
      className="rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold gap-2"
      data-testid="send-tip-btn"
    >
      <Heart className="w-4 h-4" /> Send Tip
    </Button>
  );

  if (availableMethods.length === 0) {
    return (
      <Button
        type="button"
        disabled
        className="rounded-full bg-zinc-800 text-zinc-500 cursor-not-allowed gap-2"
        title="Vendor hasn't set payment handles"
        data-testid={`tip-disabled-${source}`}
      >
        <Heart className="w-4 h-4" /> Tip
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      <DialogContent
        className="bg-zinc-950 border-white/10 text-white max-w-md"
        data-testid="tip-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl font-black tracking-tight">
            <Sparkles className="w-5 h-5 text-pink-400" />
            Send love to @{vendor.vendor_profile?.business_name || vendor.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-zinc-400">
            Tips are 100% direct to the vendor. Hidden Gem doesn't take a cut.
          </p>

          {/* Quick amounts */}
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-widest">Quick pick</Label>
            <div className="grid grid-cols-4 gap-2 mt-2">
              {QUICK_AMOUNTS.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => { setAmount(a); setCustom(""); }}
                  className={`h-12 rounded-2xl font-bold transition-all ${
                    !custom && amount === a
                      ? "bg-pink-500 text-white glow-pink"
                      : "bg-white/5 text-zinc-300 border border-white/10 hover:bg-white/10"
                  }`}
                  data-testid={`tip-amount-${a}`}
                >
                  ${a}
                </button>
              ))}
            </div>
          </div>

          {/* Custom amount */}
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-widest">Custom</Label>
            <Input
              type="number"
              step="0.01"
              min="1"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="Enter amount"
              className="mt-2 h-12 bg-white/5 border-white/10 rounded-2xl text-white"
              data-testid="tip-custom-amount"
            />
          </div>

          {/* Payment method */}
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-widest">Pay with</Label>
            <RadioGroup value={method} onValueChange={setMethod} className="mt-2 space-y-2">
              {availableMethods.map(([k, handle]) => (
                <label
                  key={k}
                  htmlFor={`tip-pm-${k}`}
                  className={`flex items-center justify-between gap-3 p-3 rounded-2xl cursor-pointer border ${
                    method === k
                      ? "bg-pink-500/10 border-pink-400/40"
                      : "bg-white/[0.02] border-white/10 hover:bg-white/5"
                  }`}
                  data-testid={`tip-method-${k}`}
                >
                  <div className="flex items-center gap-3">
                    <RadioGroupItem id={`tip-pm-${k}`} value={k} />
                    <span className="font-semibold text-sm">{PAYMENT_LABELS[k] || k}</span>
                  </div>
                  <span className="text-pink-300 text-xs font-bold truncate max-w-[140px]">{handle}</span>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-widest">Note (optional)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Loved your tacos!"
              className="mt-2 bg-white/5 border-white/10 rounded-2xl text-white min-h-[60px]"
              data-testid="tip-note"
            />
          </div>

          <button
            type="button"
            disabled={submitting}
            onClick={submit}
            className="w-full h-12 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold glow-pink disabled:opacity-50"
            data-testid="tip-submit-btn"
          >
            {submitting ? "Sending…" : `Tip $${(finalAmount || 0).toFixed(2)}`}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
